import { memo, useCallback } from 'react'

import { AutoDeleteOutlined, ControlPointDuplicate } from '@mui/icons-material'
import { MenuItem, ListItemIcon, Typography, Skeleton, Divider } from '@mui/material'
import { ImageObject } from '@nativewaves/nw-cloud-sdk/content'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { MenuItemCopyId, MenuItemDelete } from '@shared/components/MaterialUIEnhancements/Menu/MenuItem'
import { RowActionsProps } from '@shared/components/Table'
import { useImageObjectDeleteMutation, useImageObjectCreateMutation } from 'hooks/mutations/content'

const RowActionsRaw = ({ row, onClose, onClick }: RowActionsProps<ImageObject>) => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const { mutate: createImage, ...imageCreateMutation } = useImageObjectCreateMutation({})
  const { mutate: deleteImage } = useImageObjectDeleteMutation()

  const handleDuplicateImage = useCallback(() => {
    createImage(
      { imageObjectInsert: row.original },
      {
        onSuccess: () => {
          onClose()
        },
      },
    )
  }, [createImage, onClose, row.original])

  const handleDeleteImage = useCallback(() => {
    onClose()

    deleteImage(
      { imageId: row.original.id },
      {
        onSuccess: () =>
          enqueueSnackbar(t('domain:Workspace.Image.nameDelete', { name: row.original.name }), { variant: 'success' }),
        onError: () => {
          enqueueSnackbar(t('somethingWentWrong'), { variant: 'error' })
        },
      },
    )
  }, [deleteImage, enqueueSnackbar, onClose, row.original.id, row.original.name, t])

  const handleLifecycleImage = useCallback(() => {
    onClose()
    onClick?.(row)
  }, [onClick, onClose, row])

  return (
    <>
      <MenuItem onClick={handleDuplicateImage} disabled={imageCreateMutation.isPending}>
        <ListItemIcon>
          <ControlPointDuplicate />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>
          {imageCreateMutation.isPending ? <Skeleton variant="text" /> : t('duplicate')}
        </Typography>
      </MenuItem>
      <MenuItem onClick={handleLifecycleImage}>
        <ListItemIcon>
          <AutoDeleteOutlined />
        </ListItemIcon>
        <Typography sx={{ width: '100%' }}>{t('domain:Workspace.Image.setLifecyclePolicy', { count: 1 })}</Typography>
      </MenuItem>
      <MenuItemCopyId onSuccess={onClose} id={row.original.id} />
      <Divider component="li" />
      <MenuItemDelete onConfirm={handleDeleteImage} />
    </>
  )
}

export const RowActions = memo(RowActionsRaw)
